import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { usePlaidLink } from "react-plaid-link";

import { COMMAND_ID as SELECT_ENTITY_COMMAND_ID } from "@/app/app/_/commands/SelectEntityCommand";
import { Buttons } from "@/components/atoms/Buttons";
import { StickyLayout } from "@/components/organisms/StickyLayout";
import { setHotkeysSuspended } from "@/hooks/useContextTree";
import { useRestApi } from "@/hooks/useRestApi";
import { CommandCenterContextEntityPicker } from "@/layouts/AppLayout/CommandCenter/CommandCenterContextEntityPicker";
import { State } from "@/state";
import { Typography } from "@/components/ui/typography";
import { LandmarkIcon } from "lucide-react";

import { Spinner } from "@/components/ui/spinner";

function openPlaidLink(open: () => void) {
  // Plaid Link mounts a cross-origin iframe on document.body. While it is
  // open the Command Center dialog underneath still owns focus + Escape /
  // single-key hotkeys, which preventDefault keystrokes before they reach
  // Plaid. Suspend app hotkeys and release focus so Link can accept input.
  setHotkeysSuspended(true);
  (document.activeElement as HTMLElement | null)?.blur?.();
  open();
}

export const COMMAND_ID = "connect-bank-account";

export type ConnectBankAccountContextType = {
  entityIdentifier: string;
};

function Component({
  commandInstanceId,
  context,
}: {
  commandInstanceId: string;
  context?: ConnectBankAccountContextType;
}) {
  const clearCommandStack = State((store) => store.clearCommandStack);
  const popCommandsFromStack = State((store) => store.popCommandsFromStack);
  const pushToCommandStack = State((store) => store.pushToCommandStack);
  const setActiveAppView = State((store) => store.setActiveAppView);
  const setSnackbarMessage = State((store) => store.setSnackbarMessage);
  const entityIdentifier = context?.entityIdentifier;
  const [isFinished, setIsFinished] = useState(false);
  const { post, loading: restLoading } = useRestApi();

  const [linkToken, setLinkToken] = useState(null);

  // Some call sites (dashboard CTA, financial-accounts view) push this
  // command directly without first running entity selection. Without
  // ``entityIdentifier`` the link-token request can't fire and the
  // connect button spins forever -- redirect through entity selection
  // so the flow is consistent regardless of entry point.
  useEffect(() => {
    if (!entityIdentifier) {
      popCommandsFromStack();
      pushToCommandStack({
        commandDefinitionId: SELECT_ENTITY_COMMAND_ID,
        context: {
          onSubmit: (entity) => {
            pushToCommandStack({
              commandDefinitionId: COMMAND_ID,
              context: { entityIdentifier: entity.identifier },
            });
          },
        },
      });
    }
  }, [entityIdentifier, popCommandsFromStack, pushToCommandStack]);

  const generateToken = async () => {
    try {
      const params = new URLSearchParams({
        entity_identifier: entityIdentifier,
        client_user_id: entityIdentifier,
      });

      await post(
        `/v1/integrations/plaid/link-token?${params.toString()}`,
        undefined, // No body data
        {
          callbacks: {
            onSuccess: (data) => {
              setLinkToken(data.link_token);
            },
            onError: (error) => {
              console.error("Failed to create link token:", error);
              setSnackbarMessage(
                `Couldn't start Plaid: ${error}. Please try again.`,
              );
              clearCommandStack();
            },
          },
        },
      );
    } catch (error) {
      console.error("Error generating link token:", error);
    }
  };

  useEffect(() => {
    if (entityIdentifier) {
      generateToken();
    }
  }, [entityIdentifier]);

  const onSuccess = useCallback(
    async (publicToken, metadata) => {
      try {
        const params = new URLSearchParams({
          entity_identifier: entityIdentifier,
          plaid_public_token: publicToken,
          plaid_integration_institution_id: metadata.institution.institution_id,
        });

        await post(
          `/v1/integrations/plaid/exchange-public-token?${params.toString()}`,
          undefined, // No body data
          {
            callbacks: {
              onSuccess: () => {
                setIsFinished(true);
              },
              onError: (error) => {
                console.error("Failed to exchange public token:", error);
                setSnackbarMessage(
                  `Couldn't connect bank: ${error}. Please try again.`,
                );
                clearCommandStack();
              },
            },
          },
        );
      } catch (error) {
        console.error("Error exchanging public token:", error);
      }
    },
    [post, entityIdentifier, setSnackbarMessage, clearCommandStack],
  );

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit: () => {
      setHotkeysSuspended(false);
      clearCommandStack();
    },
  });

  useEffect(() => {
    return () => {
      setHotkeysSuspended(false);
    };
  }, []);

  return (
    <StickyLayout
      stickyBottomChildren={
        <Buttons
          buttons={
            !isFinished
              ? [
                  {
                    variant: "outline",
                    children: "Cancel",

                    onClick: () => {
                      setHotkeysSuspended(false);
                      clearCommandStack();
                    },
                  },
                  {
                    variant: "default",
                    children: (
                      <>
                        {(!ready || restLoading) && <Spinner />}Connect bank
                        accounts
                      </>
                    ),
                    disabled: !ready || restLoading,

                    onClick: () => {
                      openPlaidLink(() => open());
                    },
                  },
                ]
              : [
                  {
                    variant: "outline",
                    children: "Dismiss",

                    onClick: () => {
                      setIsFinished(false);
                      setHotkeysSuspended(false);
                      clearCommandStack();
                    },
                  },
                  {
                    variant: "default",
                    children: "Go to chart of accounts",

                    onClick: () => {
                      setIsFinished(false);
                      setHotkeysSuspended(false);
                      clearCommandStack();
                      setActiveAppView("chart-of-accounts");
                    },
                  },
                ]
          }
        />
      }
    >
      <Typography level="body-md" className="p-4">
        {isFinished
          ? "View your bank accounts in the chart of accounts."
          : "We partner with Plaid to provide real-time bank connectivity. On the next screen, you will be prompted to log into your bank of choice. \n\nWe do not store your credentials - In fact, we simply don't want to be responsible for them."}
      </Typography>
    </StickyLayout>
  );
}

export function ConnectBankAccountCommand() {
  const registerCommandDefinition = State(
    (state) => state.registerCommandDefinition,
  );
  const unregisterCommandDefinition = State(
    (state) => state.unregisterCommandDefinition,
  );

  useLayoutEffect(() => {
    registerCommandDefinition({
      id: COMMAND_ID,
      title: "Connect Bank Account",
      getLabel: () => "Connect Bank Account",
      description: "Connect a bank account",
      onClick: () => {
        State.getState().pushToCommandStack({
          commandDefinitionId: SELECT_ENTITY_COMMAND_ID,
          context: {
            onSubmit: (entity) => {
              State.getState().pushToCommandStack({
                commandDefinitionId: COMMAND_ID,
                context: {
                  entityIdentifier: entity.identifier,
                },
              });
            },
          },
        });
      },
      ReactIcon: LandmarkIcon,
      component: ({ commandInstanceId, context }) => (
        <Component
          commandInstanceId={commandInstanceId}
          context={context as ConnectBankAccountContextType}
        />
      ),
      // Surface the chosen entity in the dialog topbar (the same
      // little chip that ``CreateNodeCommand`` / ``UpdateNodeCommand``
      // render) so the user can see *which* entity they're about to
      // connect a bank account to without clicking back through the
      // breadcrumb.
      context: ({ context }) => {
        const ctx = context as ConnectBankAccountContextType | undefined;
        return (
          <CommandCenterContextEntityPicker
            entityIdentifier={ctx?.entityIdentifier}
          />
        );
      },
      group: "Integrations",
    });

    return () => {
      unregisterCommandDefinition(COMMAND_ID);
    };
  }, []);

  return null;
}
